<script setup lang="ts">
/**
 * THE VENUE'S DEVICES — and how a new one joins: a code, typed on the device.
 *
 * No secret is copied anywhere. The office makes a six-digit code for "the bar
 * tablet" or "the kitchen screen at the pass"; the device, on its first start,
 * asks for it. Good once and for ten minutes. Unpairing stops a device at its
 * next request. Owner and admin (`device.pair`) only; others see the list.
 */
import { computed, onMounted, reactive, ref } from "vue";
import type { ConsoleApi } from "../api";

type DevicesApi = Pick<ConsoleApi, "devices" | "pairDevice" | "unpairDevice" | "menuDraft"> &
  Partial<Pick<ConsoleApi, "setDeviceService">>;

const props = defineProps<{
  api: DevicesApi;
  me: { roleId: string } | null;
  t: (key: string) => string;
  timeZone?: string;
}>();

const devices = ref<
  readonly {
    deviceId: string;
    name: string;
    fiscalTillId: string | null;
    serviceMode?: "TABLE" | "COUNTER";
    pickupPrefix?: string | null;
  }[]
>([]);
const stations = ref<readonly { stationId: string; name: string }[]>([]);
const pairing = ref(false);
const issued = ref<{ code: string; expiresAt: Date; name: string } | null>(null);
const busy = ref(false);
const error = ref<string | null>(null);
const form = reactive({
  name: "",
  kind: "till" as "till" | "kitchen",
  stationId: "",
  serviceMode: "TABLE" as "TABLE" | "COUNTER",
});

/** The screen guests read their numbers off: a kitchen-kind screen at the "pickup" station. */
const PICKUP_STATION = "pickup";

const canPair = computed(() => props.me === null || ["owner", "admin"].includes(props.me.roleId));

const load = async (): Promise<void> => {
  try {
    devices.value = (await props.api.devices()).devices;
  } catch {
    error.value = props.t("common.failed");
  }
  try {
    stations.value = (await props.api.menuDraft()).stations;
    form.stationId ||= stations.value[0]?.stationId ?? "kitchen";
  } catch {
    // Without the stations a kitchen screen is paired to the kitchen.
  }
};
onMounted(load);

const ready = computed(() => form.name.trim().length > 0 && !busy.value);

const makeCode = async (): Promise<void> => {
  if (!ready.value) return;
  busy.value = true;
  error.value = null;
  try {
    const made = await props.api.pairDevice({
      name: form.name.trim(),
      kind: form.kind,
      ...(form.kind === "kitchen" ? { stationId: form.stationId || "kitchen" } : {}),
      // At the tables is what a till is unless said; only the counter is said.
      ...(form.kind === "till" && form.serviceMode === "COUNTER" ? { serviceMode: "COUNTER" as const } : {}),
    });
    issued.value = { code: made.code, expiresAt: new Date(made.expiresAt), name: form.name.trim() };
    pairing.value = false;
    form.name = "";
  } catch (e) {
    error.value = e instanceof Error && e.message ? e.message : props.t("common.failed");
  } finally {
    busy.value = false;
  }
};

const moveDevice = async (deviceId: string, to: "TABLE" | "COUNTER"): Promise<void> => {
  if (!props.api.setDeviceService) return;
  busy.value = true;
  error.value = null;
  try {
    await props.api.setDeviceService(deviceId, to);
    devices.value = (await props.api.devices()).devices;
  } catch (e) {
    error.value = e instanceof Error && e.message ? e.message : props.t("common.failed");
  } finally {
    busy.value = false;
  }
};

const unpair = async (deviceId: string, name: string): Promise<void> => {
  if (!window.confirm(props.t("devices.confirmUnpair").replace("{name}", name))) return;
  busy.value = true;
  try {
    await props.api.unpairDevice(deviceId);
  } catch (e) {
    error.value = e instanceof Error && e.message ? e.message : props.t("common.failed");
  } finally {
    busy.value = false;
  }
};

/** "482 913" — read aloud in two halves. */
const spaced = (code: string): string => `${code.slice(0, 3)} ${code.slice(3)}`;
const until = (at: Date): string =>
  at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", ...(props.timeZone ? { timeZone: props.timeZone } : {}) });
</script>

<template>
  <section class="gf-panel devices">
    <div class="devices__head">
      <h2 class="gf-panel__title">{{ t("devices.heading") }}</h2>
      <button v-if="canPair && !pairing" type="button" class="gf-button" data-testid="pair-device" @click="pairing = true">
        {{ t("devices.pair") }}
      </button>
    </div>

    <p v-if="error" class="devices__error" role="alert">{{ error }}</p>

    <div v-if="issued" class="devices__code" role="status" data-testid="pairing-code">
      <p class="devices__codeFor">{{ t("devices.codeFor") }} <strong>{{ issued.name }}</strong></p>
      <p class="devices__codeDigits">{{ spaced(issued.code) }}</p>
      <p class="gf-muted">{{ t("devices.codeHow") }} {{ t("devices.codeUntil") }} {{ until(issued.expiresAt) }}.</p>
      <button type="button" class="gf-button" @click="issued = null">{{ t("devices.done") }}</button>
    </div>

    <form v-if="pairing" class="devices__form" @submit.prevent="makeCode">
      <label class="gf-field">
        <span class="gf-label">{{ t("devices.name") }}</span>
        <input v-model="form.name" class="gf-input" maxlength="40" data-field="deviceName" :placeholder="t('devices.namePlaceholder')" />
      </label>
      <div class="devices__kinds" role="radiogroup" :aria-label="t('devices.kind')">
        <label class="devices__kind">
          <input v-model="form.kind" type="radio" value="till" data-field="kindTill" /> {{ t("devices.till") }}
        </label>
        <label class="devices__kind">
          <input v-model="form.kind" type="radio" value="kitchen" data-field="kindKitchen" /> {{ t("devices.kitchen") }}
        </label>
      </div>
      <div v-if="form.kind === 'till'" class="devices__kinds" role="radiogroup" :aria-label="t('devices.place')">
        <label class="devices__kind">
          <input v-model="form.serviceMode" type="radio" value="TABLE" data-field="atTables" /> {{ t("devices.atTables") }}
        </label>
        <label class="devices__kind">
          <input v-model="form.serviceMode" type="radio" value="COUNTER" data-field="atCounter" /> {{ t("devices.atCounter") }}
        </label>
      </div>
      <label v-if="form.kind === 'kitchen'" class="gf-field">
        <span class="gf-label">{{ t("devices.station") }}</span>
        <select v-model="form.stationId" class="gf-input" data-field="station">
          <option v-for="s in stations" :key="s.stationId" :value="s.stationId">{{ s.name }}</option>
          <option :value="PICKUP_STATION">{{ t("devices.pickupScreen") }}</option>
        </select>
      </label>
      <div class="devices__actions">
        <button type="button" class="gf-button" @click="pairing = false">{{ t("menu.cancel") }}</button>
        <button type="submit" class="gf-button" :disabled="!ready" data-testid="make-code">
          {{ t("devices.makeCode") }}
        </button>
      </div>
    </form>

    <p v-if="devices.length === 0 && !issued && !pairing" class="gf-muted devices__empty">{{ t("devices.none") }}</p>
    <ul class="devices__list">
      <li v-for="d in devices" :key="d.deviceId" class="devices__row">
        <span>
          {{ d.name }} <span class="gf-muted gf-mono">{{ d.fiscalTillId ?? "" }}</span>
          <span v-if="d.serviceMode === 'COUNTER'" class="gf-chip" :data-counter="d.pickupPrefix">
            {{ t("devices.counterChip").replace("{prefix}", d.pickupPrefix ?? "") }}
          </span>
        </span>
        <button
          v-if="canPair && d.fiscalTillId && api.setDeviceService"
          type="button"
          class="gf-button gf-button--ghost"
          :data-move="d.deviceId"
          :disabled="busy"
          @click="moveDevice(d.deviceId, d.serviceMode === 'COUNTER' ? 'TABLE' : 'COUNTER')"
        >
          {{ d.serviceMode === "COUNTER" ? t("devices.toTables") : t("devices.toCounter") }}
        </button>
        <button
          v-if="canPair"
          type="button"
          class="gf-button devices__unpair"
          :data-unpair="d.deviceId"
          :disabled="busy"
          @click="unpair(d.deviceId, d.name)"
        >
          {{ t("devices.unpair") }}
        </button>
      </li>
    </ul>
  </section>
</template>

<style scoped>
.devices {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-3);
}

.devices__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.devices__error {
  margin: 0;
  color: var(--gf-color-danger);
}

.devices__code {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--gf-space-2);
  padding: var(--gf-space-4);
  border-radius: var(--gf-radius-lg);
  background: var(--gf-color-surface-sunken);
}

.devices__codeFor {
  margin: 0;
}

.devices__codeDigits {
  margin: 0;
  font-size: var(--gf-text-3xl);
  font-weight: var(--gf-weight-semibold);
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.08em;
}

.devices__form {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-3);
  padding: var(--gf-space-4);
  border: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  border-radius: var(--gf-radius-lg);
}

.devices__kinds {
  display: flex;
  gap: var(--gf-space-4);
}

.devices__kind {
  display: flex;
  align-items: center;
  gap: var(--gf-space-2);
}

.devices__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--gf-space-2);
}

.devices__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.devices__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gf-space-3);
  padding: var(--gf-space-2) 0;
  border-bottom: var(--gf-border-thin) solid var(--gf-color-border-subtle);
}

.devices__row:last-child {
  border-bottom: 0;
}

.devices__empty {
  margin: 0;
  font-size: var(--gf-text-sm);
}

.devices__unpair {
  color: var(--gf-color-danger);
}
</style>
